// ===========================================
// STORAGE SERVICE
// ===========================================

import { STORAGE_KEYS, SESSION_KEYS } from '@/constants';

/**
 * Local Storage Service
 */
export const storageService = {
  /**
   * Get item from localStorage
   */
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : null;
    } catch {
      return null;
    }
  },

  /**
   * Set item in localStorage
   */
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  /**
   * Remove item from localStorage
   */
  remove(key: string): void {
    localStorage.removeItem(key);
  },

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    localStorage.clear();
  },

  /**
   * Check if key exists in localStorage
   */
  has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  },
};

/**
 * Session Storage Service
 */
export const sessionService = {
  /**
   * Get item from sessionStorage
   */
  get<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : null;
    } catch {
      return null;
    }
  },

  /**
   * Set item in sessionStorage
   */
  set<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  },

  /**
   * Remove item from sessionStorage
   */
  remove(key: string): void {
    sessionStorage.removeItem(key);
  },

  /**
   * Clear all items from sessionStorage
   */
  clear(): void {
    sessionStorage.clear();
  },
};

/**
 * Cookie Service
 */
export const cookieService = {
  /**
   * Get cookie value
   */
  get(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=').map((c) => c.trim());
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  },

  /**
   * Set cookie
   */
  set(
    name: string,
    value: string,
    options: {
      days?: number;
      path?: string;
      domain?: string;
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
    } = {}
  ): void {
    const { days = 365, path = '/', domain, secure = true, sameSite = 'Lax' } = options;

    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    cookieString += `; path=${path}`;

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += '; secure';
    }

    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
  },

  /**
   * Remove cookie
   */
  remove(name: string, path: string = '/'): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
  },
};

/**
 * Auth Token Management
 */
export const tokenService = {
  getAccessToken: () => storageService.get<string>(STORAGE_KEYS.ACCESS_TOKEN),
  setAccessToken: (token: string) => storageService.set(STORAGE_KEYS.ACCESS_TOKEN, token),
  removeAccessToken: () => storageService.remove(STORAGE_KEYS.ACCESS_TOKEN),

  getRefreshToken: () => storageService.get<string>(STORAGE_KEYS.REFRESH_TOKEN),
  setRefreshToken: (token: string) => storageService.set(STORAGE_KEYS.REFRESH_TOKEN, token),
  removeRefreshToken: () => storageService.remove(STORAGE_KEYS.REFRESH_TOKEN),

  clearTokens: () => {
    storageService.remove(STORAGE_KEYS.ACCESS_TOKEN);
    storageService.remove(STORAGE_KEYS.REFRESH_TOKEN);
  },

  hasValidToken: () => !!storageService.get<string>(STORAGE_KEYS.ACCESS_TOKEN),
};

export { STORAGE_KEYS, SESSION_KEYS };
